import { PRODUCT_SUPPORT_URL } from '@core/constant/branding';
import ArrowUpIcon from '@icon/system/regular/arrow-up.svg';
import CaretDownIcon from '@icon/system/regular/caret-down.svg';
import { For, type JSX, Show } from 'solid-js';

function EvidenceLink(props: { href: string; children: JSX.Element }) {
  return (
    <a
      href={props.href}
      target="_blank"
      rel="noreferrer"
      class="rounded-sm text-ink-muted underline decoration-ink-extra-muted underline-offset-4 hover:text-ink focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-ink"
    >
      {props.children}
    </a>
  );
}

/** Connection behavior and operator contact, without inherited provider assurances. */
export function OnboardingTrustDetails(props: {
  topic: 'security' | 'google';
}) {
  const google = () => props.topic === 'google';
  const questions = () =>
    google()
      ? [
          {
            title: 'Why does Google ask for these permissions?',
            answer: (
              <>
                Gmail access lets Atlas read, send, organize, and move messages
                to trash. Contacts help suggest recipients; mail settings
                support your signature and filters. When you connect Calendar,
                its permissions let Atlas show your calendars and create or
                update events. Google shows the exact permissions before you
                approve.
              </>
            ),
          },
          {
            title: 'Does Atlas get my Google password?',
            answer: (
              <>
                No. You sign in and approve access on Google. Google gives Atlas
                authorization for the permissions you approve; your Google
                password stays with Google.
              </>
            ),
          },
          {
            title: 'What happens when I use an AI agent?',
            answer: (
              <>
                Information needed for your request is sent to the configured
                model provider. Contact Solidum Invest for the data processing
                terms that apply to your workspace.
              </>
            ),
          },
          {
            title: 'How do I revoke access?',
            answer: (
              <>
                You can disconnect in Atlas’s Settings or revoke access from
                your Google account. Revoking access and deleting data already
                stored in Atlas are separate actions. You can request account
                and data deletion by contacting{' '}
                <EvidenceLink href={PRODUCT_SUPPORT_URL}>
                  Atlas support
                </EvidenceLink>
                .
              </>
            ),
          },
        ]
      : [
          {
            title: 'Who provides Atlas?',
            answer: <>Atlas is provided by Solidum Invest.</>,
          },
          {
            title: 'What happens when I use an AI agent?',
            answer: (
              <>
                Information needed for your request is sent to the configured
                model provider. Data processing terms depend on the provider and
                this workspace's configuration.
              </>
            ),
          },
          {
            title: 'Where can I ask about data protection?',
            answer: (
              <>
                Contact{' '}
                <EvidenceLink href={PRODUCT_SUPPORT_URL}>
                  Atlas support
                </EvidenceLink>{' '}
                for the policies and data processing terms that apply to your
                workspace.
              </>
            ),
          },
        ];

  return (
    <section class="mx-auto w-full max-w-[608px] px-6 pb-20 pt-20 font-[Inter_Variable] text-sm leading-7 text-ink-muted sm:pb-28 sm:pt-24 sm:text-[15px]">
      <div class="mb-10 h-px w-12 bg-edge" aria-hidden="true" />
      <p class="mb-4 text-xs text-ink-extra-muted">
        {google() ? 'Google account connection' : 'Security & privacy'}
      </p>
      <h2
        tabindex="-1"
        class="mb-7 text-2xl font-medium leading-8 tracking-tight text-ink outline-none sm:text-[28px] sm:leading-9"
      >
        {google() ? 'Email and calendar permissions' : 'Data protection'}
      </h2>
      <div class="space-y-5">
        <Show
          when={google()}
          fallback={
            <>
              <p>
                Atlas stores your workspace content to provide the service.
                Connected accounts grant the permissions you approve at the
                provider.
              </p>
              <p>
                Contact Solidum Invest for information about this workspace's
                hosting, data processing, and applicable policies.
              </p>
            </>
          }
        >
          <p>
            Atlas requests Google account access to read, send, and organize
            email and manage connected calendars. Google lists the permissions
            before you approve the connection.
          </p>
          <p>
            Atlas’s email agents show you the draft and ask for your approval
            before sending.
          </p>
          <p>
            You sign in through Google. Atlas does not receive your Google
            password. Your Atlas sign-in stays the same; adding a personal
            account is optional. You can disconnect in Atlas’s Settings or
            revoke access from your Google account.
          </p>
        </Show>
      </div>
      <h3 class="mb-3 mt-12 text-base font-medium text-ink">
        Frequently asked questions
      </h3>
      <div class="border-t border-edge-muted">
        <For each={questions()}>
          {(question) => (
            <details class="group border-b border-edge-muted">
              <summary class="flex list-none items-start justify-between gap-5 rounded-sm py-5 text-sm font-medium leading-6 text-ink-muted hover:text-ink focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-ink [&::-webkit-details-marker]:hidden">
                {question.title}
                <CaretDownIcon
                  class="mt-1 size-4 shrink-0 text-ink-extra-muted transition-transform group-open:rotate-180 motion-reduce:transition-none"
                  aria-hidden="true"
                />
              </summary>
              <p class="pb-6 pr-6 font-normal">{question.answer}</p>
            </details>
          )}
        </For>
      </div>
      <p class="mt-8 text-xs leading-6 text-ink-extra-muted">
        Questions about your data? Contact{' '}
        <EvidenceLink href={PRODUCT_SUPPORT_URL}>Atlas support</EvidenceLink>.
      </p>
      <button
        type="button"
        class="mt-10 inline-flex items-center gap-2 rounded-sm py-2 text-sm text-ink-muted hover:text-ink focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-ink"
        onClick={(event) => {
          const scroller = event.currentTarget.closest(
            '[data-onboarding-scroll]'
          );
          scroller
            ?.querySelector<HTMLElement>('h1')
            ?.focus({ preventScroll: true });
          scroller?.scrollTo({
            top: 0,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)')
              .matches
              ? 'instant'
              : 'smooth',
          });
        }}
      >
        Back to setup <ArrowUpIcon class="size-4" aria-hidden="true" />
      </button>
    </section>
  );
}
